import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw } from 'lucide-react';
import { getWorkspaceAutomationHealth, type WorkspaceAutomationHealth } from '@/lib/lendliftedService';

interface AutomationHealthCardProps {
  workspaceId: string;
}

export const AutomationHealthCard: React.FC<AutomationHealthCardProps> = ({ workspaceId }) => {
  const [data, setData] = useState<WorkspaceAutomationHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const next = await getWorkspaceAutomationHealth(workspaceId, 7);
      setData(next);
    } catch (e: any) {
      setError(e?.message || 'Failed to load automation health.');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [workspaceId]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-xl tracking-[-0.02em] text-slate-950">Automation Health</CardTitle>
            <CardDescription className="text-slate-600">
              Recent webhook delivery outcomes and widget click activity (last 7 days).
            </CardDescription>
          </div>
          <Button type="button" variant="outline" size="sm" className="rounded-xl border-slate-200 bg-white hover:bg-slate-50" onClick={() => load()} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {error ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        ) : isLoading && !data ? (
          <div className="space-y-3">
            <div className="h-14 animate-pulse rounded-lg bg-slate-100" />
            <div className="h-24 animate-pulse rounded-lg bg-slate-100" />
          </div>
        ) : data ? (
          <>
            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="text-xs text-slate-500">Webhooks</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{data.webhooks.configured ? 'Configured' : 'Not configured'}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="text-xs text-slate-500">Delivered</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{data.webhooks.summary.delivered}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="text-xs text-slate-500">Failed</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{data.webhooks.summary.failed}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="text-xs text-slate-500">Widget clicks</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{data.widget.totalClicks}</p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm font-medium text-slate-900">Widget click modes</p>
              {data.widget.clickTotalsByMode.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No widget clicks recorded in this period.</p>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  {data.widget.clickTotalsByMode.map((row) => (
                    <Badge key={row.mode} variant="secondary">
                      {row.mode}: {row.count}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm font-medium text-slate-900">Recent webhook deliveries</p>
              {data.webhooks.recentDeliveries.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No deliveries yet.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {data.webhooks.recentDeliveries.slice(0, 8).map((row) => (
                    <div key={row.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={row.status === 'delivered' ? 'default' : row.status === 'failed' ? 'destructive' : 'secondary'}>
                            {row.status}
                          </Badge>
                          <span className="text-xs text-slate-600">{row.eventType}</span>
                          {row.responseStatus ? <span className="text-xs text-slate-500">HTTP {row.responseStatus}</span> : null}
                        </div>
                        <span className="text-xs text-slate-500">
                          {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : 'n/a'}
                        </span>
                      </div>
                      {row.errorMessage ? <p className="mt-1 text-xs text-red-700">{row.errorMessage}</p> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};
